import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export interface ActivePlanBar {
  paymentId: string;
  planName: string | null;
  gymId: string;
  createdAt: string;
  expiresAt: string | null;
  remainingEntries: number | null;
  isExpired: boolean;
  isFixedPayment: boolean;
  isClass: boolean;
}

export interface UserActivePlansRow {
  userId: string;
  name: string;
  surname: string;
  email: string | null;
  payments: ActivePlanBar[];
}

export interface ActivePlansTimelinePage {
  users: UserActivePlansRow[];
  nextCursor: string | null;
}

export const useActivePlansTimeline = (limit = 50) =>
  useInfiniteQuery({
    queryKey: ['analytics', 'active-plans-timeline', limit],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number> = { limit };
      if (pageParam) params.cursor = pageParam;
      const response = await apiClient.get(
        '/reports/users-active-plans-timeline',
        { params },
      );
      return response.data as ActivePlansTimelinePage;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

export interface ExpirationItem {
  userId: string;
  name: string;
  surname: string;
  email: string | null;
  paymentId: string;
  planName: string | null;
  expiresAt: string;
  isClass: boolean;
  isFixedPayment: boolean;
  remainingEntries: number | null;
}

export interface ExpirationDay {
  date: string; // YYYY-MM-DD
  count: number;
  gymCount: number;
  classCount: number;
  items: ExpirationItem[];
}

export interface UpcomingExpirationsReport {
  from: string;
  to: string;
  today: string;
  total: number;
  gymTotal: number;
  classTotal: number;
  days: ExpirationDay[];
}

export const useUpcomingExpirations = (from: string, to: string) =>
  useQuery({
    queryKey: ['analytics', 'upcoming-expirations', from, to],
    queryFn: async () => {
      const response = await apiClient.get('/reports/upcoming-expirations', {
        params: { from, to },
      });
      return response.data as UpcomingExpirationsReport;
    },
  });
